import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Hourglass, Download, Loader2, Info } from 'lucide-react';
import { api, downloadBlob } from '@/api/client';

type OT = 'ALL' | 'B2C' | 'B2B';
type Stage = 'all' | 'forward' | 'rto' | 'pickup';
type GroupBy = 'courier' | 'brand' | 'merchant';
type BucketKey = 'b1' | 'b2' | 'b3' | 'b4' | 'b5';
interface Row {
  key: string; name: string;
  courierIds: (number | null)[] | null; merchantIds: number[] | null;
  b1: number; b2: number; b3: number; b4: number; b5: number; total: number;
}
interface Report {
  stage: Stage; orderType: OT; groupBy: GroupBy;
  buckets: { key: BucketKey; label: string }[];
  rows: Row[]; totals: Record<BucketKey | 'total', number>; asOf: string;
}

const STAGES: { id: Stage; label: string; hint: string }[] = [
  { id: 'all', label: 'Not delivered + RTO not back', hint: 'Everything still with a courier' },
  { id: 'forward', label: 'Not delivered', hint: 'Picked up, still moving to the customer' },
  { id: 'rto', label: 'RTO not back', hint: 'Returning, not yet delivered back' },
  { id: 'pickup', label: 'Not picked up', hint: 'AWB generated, the courier never picked it up' },
];
// Older = redder, so the eye goes to the 45+ column first.
const TINT: Record<BucketKey, string> = {
  b1: 'bg-amber-50 text-amber-800 hover:bg-amber-100',
  b2: 'bg-amber-100 text-amber-900 hover:bg-amber-200',
  b3: 'bg-orange-100 text-orange-900 hover:bg-orange-200',
  b4: 'bg-rose-100 text-rose-800 hover:bg-rose-200',
  b5: 'bg-rose-200 text-rose-900 hover:bg-rose-300',
};
const num = (n: number) => Number(n ?? 0).toLocaleString('en-IN');

/**
 * Pending shipments by age — which courier (or merchant) is sitting on shipments, and how long.
 *
 * Live, not a date range: a shipment stuck for 40 days is stuck today. Every number is a button that
 * downloads exactly those shipments (AWB, courier, status, days pending, last courier remark …).
 *
 * `scope="admin"`: company-wide, grouped by courier service, courier company or merchant.
 * `scope="merchant"`: the merchant dashboard — the server limits it to the logged-in merchant's own
 * shipments (from the token), so there is no merchant grouping and nothing to choose.
 */
export function PendingReportPanel({ ot, scope = 'admin', defaultStage = 'all' }: { ot: OT; scope?: 'admin' | 'merchant'; defaultStage?: Stage }) {
  const [stage, setStage] = useState<Stage>(defaultStage);
  const [groupBy, setGroupBy] = useState<GroupBy>('courier');
  const [busy, setBusy] = useState<string | null>(null);
  const base = scope === 'merchant' ? '/dashboard/merchant/pending-report' : '/dashboard/admin/pending-report';
  const groupOptions: { id: GroupBy; label: string }[] = scope === 'merchant'
    ? [{ id: 'courier', label: 'By service' }, { id: 'brand', label: 'By courier company' }]
    : [{ id: 'courier', label: 'By service' }, { id: 'brand', label: 'By courier company' }, { id: 'merchant', label: 'By merchant' }];

  const { data, isLoading } = useQuery<Report>({
    queryKey: ['pending-report', scope, ot, stage, groupBy],
    queryFn: async () => (await api.get<Report>(base, { params: { orderType: ot, stage, groupBy } })).data,
    staleTime: 60_000,
  });

  const download = async (cellId: string, row: Row | null, bucket: BucketKey | null, label: string) => {
    setBusy(cellId);
    try {
      const params: Record<string, string> = { orderType: ot, stage };
      if (row?.merchantIds?.length) params.merchantIds = row.merchantIds.join(',');
      else if (row?.courierIds) params.courierIds = row.courierIds.map((c) => (c == null ? 'none' : String(c))).join(',');
      if (bucket) params.bucket = bucket;
      const qs = new URLSearchParams(params).toString();
      const safe = label.replace(/[^a-z0-9]+/gi, '-').replace(/^-|-$/g, '').toLowerCase();
      await downloadBlob(`${base}/export?${qs}`, `pending-${stage}-${ot.toLowerCase()}-${safe}-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (e: any) {
      toast.error(e?.response?.data?.message ?? 'Export failed');
    } finally {
      setBusy(null);
    }
  };

  const buckets = data?.buckets ?? [];
  const shownGroup = data?.groupBy ?? groupBy;
  const Cell = ({ id, value, row, bucket, label, tint, strong }: {
    id: string; value: number; row: Row | null; bucket: BucketKey | null; label: string; tint?: string; strong?: boolean;
  }) => (
    value > 0 ? (
      <button onClick={() => download(id, row, bucket, label)} disabled={busy !== null}
        title={`Download these ${num(value)} shipments (CSV)`}
        className={`inline-flex items-center justify-end gap-1 min-w-[64px] px-2 py-1 rounded-md text-sm tabular-nums transition disabled:opacity-60 ${tint ?? 'hover:bg-slate-100 text-slate-800'} ${strong ? 'font-semibold' : ''}`}>
        {busy === id ? <Loader2 size={12} className="animate-spin" /> : <Download size={11} className="opacity-50" />}
        {num(value)}
      </button>
    ) : <span className="inline-block min-w-[64px] px-2 py-1 text-right text-slate-300 tabular-nums">0</span>
  );

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-semibold text-slate-800 text-sm flex items-center gap-1.5">
            <Hourglass size={16} className="text-rose-500" />
            {scope === 'merchant' ? 'Your pending shipments by age' : `Pending shipments by age — ${shownGroup === 'merchant' ? 'merchant' : 'courier'}-wise`}
          </h3>
          <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1">
            <Info size={11} /> Live as of now{scope === 'admin' ? ' — the date range above does not apply' : ''}. Click any number to download those shipments.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {STAGES.map((s) => (
              <button key={s.id} onClick={() => setStage(s.id)} title={s.hint}
                className={`px-3 py-1 text-xs rounded-md transition ${stage === s.id ? 'bg-white shadow-sm text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
                {s.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            {groupOptions.map((g) => (
              <button key={g.id} onClick={() => setGroupBy(g.id)}
                className={`px-3 py-1 text-xs rounded-md transition ${groupBy === g.id ? 'bg-white shadow-sm text-primary font-medium' : 'text-slate-600 hover:text-slate-900'}`}>
                {g.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">{shownGroup === 'merchant' ? 'Merchant' : 'Courier'}</th>
              {buckets.map((b) => <th key={b.key} className="text-right px-3 py-2 whitespace-nowrap">{b.label}</th>)}
              <th className="text-right px-3 py-2">Total</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400"><Loader2 size={18} className="mx-auto animate-spin" /></td></tr>
            )}
            {!isLoading && (data?.rows.length ?? 0) === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-slate-400">Nothing pending for 4 days or more.</td></tr>
            )}
            {(data?.rows ?? []).map((r) => (
              <tr key={r.key} className="border-t border-slate-100">
                <td className="px-3 py-1.5 text-slate-800">{r.name}</td>
                {buckets.map((b) => (
                  <td key={b.key} className="px-2 py-1 text-right">
                    <Cell id={`${r.key}-${b.key}`} value={r[b.key]} row={r} bucket={b.key} label={`${r.name}-${b.label}`} tint={TINT[b.key]} />
                  </td>
                ))}
                <td className="px-2 py-1 text-right">
                  <Cell id={`${r.key}-total`} value={r.total} row={r} bucket={null} label={`${r.name}-all-ages`} strong />
                </td>
              </tr>
            ))}
          </tbody>
          {data && data.rows.length > 0 && (
            <tfoot className="border-t-2 border-slate-200 bg-slate-50">
              <tr>
                <td className="px-3 py-2 font-semibold text-slate-800">{shownGroup === 'merchant' ? 'All merchants' : 'All couriers'}</td>
                {buckets.map((b) => (
                  <td key={b.key} className="px-2 py-1 text-right">
                    <Cell id={`all-${b.key}`} value={data.totals[b.key]} row={null} bucket={b.key} label={`all-${b.label}`} strong />
                  </td>
                ))}
                <td className="px-2 py-1 text-right">
                  <Cell id="all-total" value={data.totals.total} row={null} bucket={null} label="all-ages" strong />
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      <p className="text-[11px] text-slate-400 mt-2">
        Days are counted from the ship date; an RTO from the day it turned back. Under 4 days is normal transit and is not listed.
        {stage === 'pickup' && ' "Not picked up" = the AWB exists but the courier never collected the parcel — hand it over, or cancel it so it stops holding a waybill.'}
      </p>
    </div>
  );
}
